import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';
import { styled } from '@mui/material/styles';
import { FormattedMessage, FormattedNumber } from 'react-intl';

const PreviousLabel = () => (
  <FormattedMessage
    id="auditLog.pagination.previous"
    defaultMessage="‹ Previous"
  />
);

const NextLabel = () => (
  <FormattedMessage
    id="auditLog.pagination.next"
    defaultMessage="Next ›"
  />
);

const AuditLogPagination = ({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) => {
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <Root>
      <Range>
        <FormattedMessage
          id="auditLog.pagination.range"
          defaultMessage="Showing {from}–{to} of {total}"
          values={{
            from: <strong>{from}</strong>,
            to: <strong>{to}</strong>,
            total: (
              <strong>
                <FormattedNumber value={total} />
              </strong>
            ),
          }}
        />
      </Range>
      <Pagination
        page={page}
        count={Math.ceil(total / pageSize)}
        onChange={(_, value) => onPageChange(value)}
        shape="rounded"
        renderItem={(item) => (
          <StyledPaginationItem
            slots={{ previous: PreviousLabel, next: NextLabel }}
            {...item}
          />
        )}
      />
    </Root>
  );
};

const Root = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  padding: theme.spacing(1.75, 2.25),
  borderTop: `1px solid ${theme.palette.grey200}`,
  background: theme.palette.grey50,
}));

const Range = styled('div')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.textSecondaryDarkGrey,
}));

const StyledPaginationItem = styled(PaginationItem)(({ theme }) => ({
  fontSize: 13,
  '&.MuiPaginationItem-previousNext': {
    width: 'auto',
    padding: theme.spacing(0, 1.5),
    border: `1px solid ${theme.palette.grey200}`,
    background: theme.palette.white,
  },
  '&.Mui-selected': {
    background: theme.palette.primary500,
    color: theme.palette.textPrimaryWhite,
    fontWeight: 600,
  },
  '&.Mui-selected:hover': {
    background: theme.palette.primary500,
  },
}));

export default AuditLogPagination;
